import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import { SCROLL_MAP } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import {
  TRADE_IN_APPRAISER_NAME,
  TRADE_IN_APPRAISER_HEADER,
  REPORT,
} from 'site-modules/shared/constants/appraisal/appraisal';

export function useAppraiserScroll({ isExtendedReport, isMobile, getActiveStep }) {
  const scrollCompletionCleanupRef = useRef(null);
  const scrollTo = useCallback(
    (selector, scrollIntoViewOptions) => {
      if (document) {
        const elem = document.querySelector(selector);
        if (elem) {
          elem.scrollIntoView({
            behavior: isExtendedReport ? 'auto' : 'smooth',
            block: 'start',
            ...scrollIntoViewOptions,
          });
        }
      }
    },
    [isExtendedReport]
  );

  const scrollToNextStep = useCallback(() => {
    const activeStep = getActiveStep();
    scrollTo(`.${TRADE_IN_APPRAISER_NAME} .${activeStep.id}`);
  }, [getActiveStep, scrollTo]);

  const scrollToReportStep = useCallback(() => {
    setTimeout(() => {
      scrollTo(`.${TRADE_IN_APPRAISER_NAME} .${REPORT}`);
    }, 1000);
  }, [scrollTo]);

  const scrollToTradeInTitle = useCallback(() => {
    if (isMobile) {
      scrollTo(`[name="${TRADE_IN_APPRAISER_HEADER}"]`);
    }
  }, [isMobile, scrollTo]);

  const scrollToInvalidWithMap = useCallback(
    errorMap => {
      for (let i = 0; i < SCROLL_MAP.length; i += 1) {
        const scrollObj = SCROLL_MAP[i];
        if (errorMap.has(scrollObj.input)) {
          scrollTo(`.${TRADE_IN_APPRAISER_NAME} ${scrollObj.selector}`, scrollObj.options);
          return;
        }
      }
    },
    [scrollTo]
  );

  const debouncedScrollToStep = useMemo(
    () => debounce(stepId => scrollTo(`.${TRADE_IN_APPRAISER_NAME} .${stepId}`), 1),
    [scrollTo]
  );

  const smoothScrollToStep = useCallback((stepId, onScrollComplete) => {
    scrollCompletionCleanupRef.current?.();
    scrollCompletionCleanupRef.current = null;

    const elem = document.querySelector(`.${TRADE_IN_APPRAISER_NAME} .${stepId}`);
    if (!elem) {
      onScrollComplete?.();
      return;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';
    elem.scrollIntoView({ behavior, block: 'start' });

    if (!onScrollComplete) {
      return;
    }
    if (behavior !== 'smooth' || Math.abs(elem.getBoundingClientRect().top) <= 1) {
      onScrollComplete();
      return;
    }

    const isTargetAligned = () => Math.abs(elem.getBoundingClientRect().top) <= 1;

    let fallbackTimerId;
    let handleScrollEnd;

    const finish = () => {
      window.removeEventListener('scrollend', handleScrollEnd);
      clearTimeout(fallbackTimerId);
      scrollCompletionCleanupRef.current = null;
      onScrollComplete();
    };

    handleScrollEnd = () => {
      if (!isTargetAligned()) {
        return;
      }
      finish();
    };

    fallbackTimerId = setTimeout(() => {
      if (isTargetAligned()) {
        finish();
      }
    }, 1000);

    window.addEventListener('scrollend', handleScrollEnd);

    scrollCompletionCleanupRef.current = () => {
      window.removeEventListener('scrollend', handleScrollEnd);
      clearTimeout(fallbackTimerId);
    };
  }, []);

  // Cancel debounce when component unmounts to prevent memory leakage and unwanted scrolls
  useEffect(
    () => () => {
      debouncedScrollToStep.cancel();
      scrollCompletionCleanupRef.current?.();
    },
    [debouncedScrollToStep]
  );

  return {
    scrollTo,
    scrollToNextStep,
    scrollToReportStep,
    scrollToTradeInTitle,
    scrollToInvalidWithMap,
    debouncedScrollToStep,
    smoothScrollToStep,
  };
}
